"use client";

import { c as _c } from "react/compiler-runtime";
import { CALL_ENDED_MESSAGE, callAnnouncement, captionLine, type LevelBarsMode, type LiveCallSubscribable, levelBarScales, levelBarsMode, type MagnusLiveCallSource, phaseLabel, useLiveCallElapsed, useLiveCallLevels } from "@bg-bgi/magnus-sdk/react";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@workspace/ui/components/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@workspace/ui/components/tooltip";
import { cn } from "@workspace/ui/lib/utils";
import { EllipsisIcon, MicIcon, MicOffIcon, PhoneOffIcon, XIcon } from "lucide-react";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { type FocusEvent, type MouseEvent, type ReactElement, useRef } from "react";

/** What the pill reads from a live-call source. */
export type LiveCallPillSource = Pick<MagnusLiveCallSource, "captions" | "captionsOn" | "delegatingQuery" | "error" | "inputDeviceId" | "inputDevices" | "isMuted" | "meter" | "phase" | "setCaptionsOn" | "setInputDevice" | "setMuted" | "setVoice" | "startedAt" | "stop" | "voice" | "voices">;
const DEFAULT_DEVICE = "__default__";
const SURFACE = "border border-border bg-popover text-popover-foreground shadow-[var(--elevation-sm),var(--elevation-lg)]";
const PILL_WIDTH = "w-[364px] max-w-full";
const PILL_SIZE = `h-11 ${PILL_WIDTH}`;
export type CaptionsPlacement = "above" | "below" | "inline";
const CAPTIONS_PLACEMENT: Record<CaptionsPlacement, string> = {
  above: "absolute inset-x-0 bottom-full mb-2",
  below: "absolute inset-x-0 top-full mt-2",
  inline: "mt-2"
};
const FADE = {
  initial: {
    opacity: 0,
    y: -3
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -3
  },
  transition: {
    duration: 0.14,
    ease: "easeOut"
  }
} as const;
const ICON_BUTTON = "inline-flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 aria-expanded:bg-muted aria-expanded:text-foreground aria-pressed:bg-muted aria-pressed:text-foreground motion-reduce:transition-none";

/**
 * Where focus came from before it entered the pill, so End and Dismiss —
 * which unmount the control holding focus — can hand it back instead of
 * dropping it on `<body>`.
 */
function useFocusReturn() {
  const $ = _c(2);
  const from = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = event => {
      const prev = event.relatedTarget;
      if (prev instanceof HTMLElement && !event.currentTarget.contains(prev) && !prev.closest("[role=menu]")) {
        from.current = prev;
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const onFocus = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    const handBack = inside => {
      const target = from.current;
      if (inside?.contains(document.activeElement) && target?.isConnected && !inside.contains(target)) {
        target.focus();
      }
    };
    t1 = {
      onFocus,
      handBack
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function WithTooltip(t0) {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <TooltipContent side="bottom" sideOffset={8}>{label}</TooltipContent>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <TooltipTrigger render={children} />;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/** The pill's one subscriber to the meter's ~60 ms level ticks. */
function LevelBars(t0) {
  const $ = _c(6);
  const {
    meter,
    muted,
    mode
  } = t0;
  const scales = levelBarScales(mode, useLiveCallLevels(meter), muted);
  const t1 = true;
  const t2 = mode === "output" ? "text-foreground" : "text-muted-foreground";
  const t3 = mode === "busy" && "animate-pulse motion-reduce:animate-none";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = cn("flex h-3 w-[17px] shrink-0 items-center gap-[3px]", t2, t3);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = scales.map(_temp);
  let t6;
  if ($[3] !== t4 || $[4] !== t5) {
    t6 = <div aria-hidden={t1} className={t4}>{t5}</div>;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  return t6;
}
function _temp(scale, i) {
  return <span className="h-3 w-[2px] rounded-full bg-current transition-transform duration-200 ease-out motion-reduce:transition-none" key={i} style={{
    transform: `scaleY(${scale})`
  }} />;
}
function CallMenu(t0) {
  const $ = _c(31);
  const {
    call,
    captionsOn,
    onCaptionsChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <WithTooltip label="Call options"><DropdownMenuTrigger render={<button aria-label="Call options" className={ICON_BUTTON} type="button"><EllipsisIcon className="size-4" /></button>} /></WithTooltip>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onCaptionsChange) {
    t2 = checked => onCaptionsChange(checked);
    $[1] = onCaptionsChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== captionsOn || $[4] !== t2) {
    t3 = <DropdownMenuCheckboxItem checked={captionsOn} onCheckedChange={t2}>Show captions</DropdownMenuCheckboxItem>;
    $[3] = captionsOn;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DropdownMenuSeparator />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DropdownMenuLabel>Microphone</DropdownMenuLabel>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== call) {
    t6 = value => {
      call.setInputDevice(value === DEFAULT_DEVICE ? null : value).catch(_temp2);
    };
    $[8] = call;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = call.inputDeviceId ?? DEFAULT_DEVICE;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DropdownMenuRadioItem value={DEFAULT_DEVICE}>System default</DropdownMenuRadioItem>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== call.inputDevices) {
    t9 = call.inputDevices.filter(_temp3).map(_temp4);
    $[11] = call.inputDevices;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t6 || $[14] !== t7 || $[15] !== t9) {
    t10 = <DropdownMenuGroup>{t5}<DropdownMenuRadioGroup onValueChange={t6} value={t7}>{t8}{t9}</DropdownMenuRadioGroup></DropdownMenuGroup>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DropdownMenuSeparator />;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DropdownMenuLabel>Voice</DropdownMenuLabel>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== call) {
    t13 = value_0 => call.setVoice(value_0);
    $[19] = call;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = call.voice;
  let t15;
  if ($[21] !== call.voices) {
    t15 = call.voices.map(_temp5);
    $[21] = call.voices;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== call.voice || $[24] !== t13 || $[25] !== t15) {
    t16 = <DropdownMenuGroup>{t12}<DropdownMenuRadioGroup onValueChange={t13} value={t14}>{t15}</DropdownMenuRadioGroup></DropdownMenuGroup>;
    $[23] = call.voice;
    $[24] = t13;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t10 || $[28] !== t16 || $[29] !== t3) {
    t17 = <DropdownMenu>{t1}<DropdownMenuContent align="end" className="w-60 rounded-xl" side="bottom" sideOffset={8}>{t3}{t4}{t10}{t11}{t16}</DropdownMenuContent></DropdownMenu>;
    $[27] = t10;
    $[28] = t16;
    $[29] = t3;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  return t17;
}
function _temp5(option) {
  return <DropdownMenuRadioItem key={option.id} value={option.id}>{option.label}</DropdownMenuRadioItem>;
}
function _temp4(device) {
  return <DropdownMenuRadioItem key={device.deviceId} value={device.deviceId}><span className="truncate">{device.label || "Unnamed microphone"}</span></DropdownMenuRadioItem>;
}
function _temp3(d) {
  return d.deviceId && d.deviceId !== "default";
}
function _temp2() {}
function Captions(t0) {
  const $ = _c(14);
  const {
    call,
    placement
  } = t0;
  let line;
  let t1;
  let t2;
  if ($[0] !== call.captions || $[1] !== call.delegatingQuery || $[2] !== placement) {
    line = captionLine(call.captions, call.delegatingQuery);
    const t3 = CAPTIONS_PLACEMENT[placement];
    if ($[6] !== t3) {
      t2 = cn(SURFACE, "pointer-events-auto rounded-2xl px-4 py-2.5", t3);
      $[6] = t3;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = cn("line-clamp-2 min-h-10 text-[13px] leading-5", line.muted && "text-muted-foreground");
    $[0] = call.captions;
    $[1] = call.delegatingQuery;
    $[2] = placement;
    $[3] = line;
    $[4] = t1;
    $[5] = t2;
  } else {
    line = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[8] !== line.text || $[9] !== t1) {
    t3 = <p className={t1}>{line.text}</p>;
    $[8] = line.text;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}

/**
 * The live call's controls (plan 042): level meter, phase, elapsed time,
 * mute, an options menu (captions, microphone, voice) and End. A failed or
 * refused call shows its message with a Dismiss instead. Renders nothing
 * while idle or connecting — the composer's call button carries those.
 */
export function LiveCallPill(t0) {
  const $ = _c(20);
  const {
    call,
    captions: t1
  } = t0;
  const captions = t1 === undefined ? "below" : t1;
  const {
    captionsOn,
    setCaptionsOn
  } = call;
  const elapsed = useLiveCallElapsed(call.startedAt);
  const focus = useFocusReturn();
  let t2;
  if ($[0] !== call || $[1] !== focus) {
    t2 = event => {
      focus.handBack(event.currentTarget.closest("[data-call-pill]"));
      call.stop();
    };
    $[0] = call;
    $[1] = focus;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const end = t2;
  const errored = call.phase === "errored" || call.phase === "idle" && call.error !== null;
  const open = call.phase !== "idle" && call.phase !== "errored" && call.phase !== "connecting";
  let t3;
  if ($[3] !== call.error || $[4] !== end || $[5] !== errored || $[6] !== focus.onFocus) {
    t3 = errored && <motion.div animate={FADE.animate} className={cn(SURFACE, PILL_SIZE, "pointer-events-auto flex items-center gap-2 rounded-full p-1 pl-4")} data-call-pill="" exit={FADE.exit} initial={FADE.initial} key="errored" onFocus={focus.onFocus} transition={FADE.transition}><span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground" role="status">{call.error ?? CALL_ENDED_MESSAGE}</span><WithTooltip label="Dismiss"><button aria-label="Dismiss" className={ICON_BUTTON} onClick={end} type="button"><XIcon className="size-4" /></button></WithTooltip></motion.div>;
    $[3] = call.error;
    $[4] = end;
    $[5] = errored;
    $[6] = focus.onFocus;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== call || $[9] !== captions || $[10] !== captionsOn || $[11] !== elapsed || $[12] !== end || $[13] !== focus.onFocus || $[14] !== open || $[15] !== setCaptionsOn) {
    t4 = open && <motion.div animate={FADE.animate} className={cn(PILL_WIDTH, "relative")} data-call-pill="" exit={FADE.exit} initial={FADE.initial} key="call" onFocus={focus.onFocus} transition={FADE.transition}><section aria-label="Voice call" className={cn(SURFACE, "pointer-events-auto flex h-11 w-full items-center rounded-full p-1")}><div className="flex min-w-0 flex-1 items-center gap-2.5 pr-2 pl-3"><LevelBars meter={call.meter} mode={levelBarsMode(call.phase)} muted={call.isMuted} /><span className="sr-only" role="status">{callAnnouncement(call.phase, call.isMuted, call.delegatingQuery)}</span><span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">{phaseLabel(call.phase, call.isMuted)}</span><span className="w-[5ch] shrink-0 text-right text-[13px] text-muted-foreground/70 tabular-nums">{elapsed}</span></div><WithTooltip label={call.isMuted ? "Unmute" : "Mute"}><button aria-label={call.isMuted ? "Unmute" : "Mute"} aria-pressed={call.isMuted} className={ICON_BUTTON} onClick={() => call.setMuted(!call.isMuted)} type="button">{call.isMuted ? <MicOffIcon className="size-4" /> : <MicIcon className="size-4" />}</button></WithTooltip><CallMenu call={call} captionsOn={captionsOn} onCaptionsChange={setCaptionsOn} /><button className="ml-0.5 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-destructive/10 px-3 font-medium text-[13px] text-destructive outline-none transition-colors duration-150 hover:bg-destructive/15 focus-visible:ring-2 focus-visible:ring-destructive/40 motion-reduce:transition-none" onClick={end} type="button"><PhoneOffIcon className="size-4" />End</button></section>{captionsOn && <Captions call={call} placement={captions} />}</motion.div>;
    $[8] = call;
    $[9] = captions;
    $[10] = captionsOn;
    $[11] = elapsed;
    $[12] = end;
    $[13] = focus.onFocus;
    $[14] = open;
    $[15] = setCaptionsOn;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t3 || $[18] !== t4) {
    t5 = <MotionConfig reducedMotion="user"><AnimatePresence initial={false}>{t3}{t4}</AnimatePresence></MotionConfig>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}